import { Variable } from "lucide-react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { inputVariableChipClassName } from "@/components/InputVariable/input-variable-styles";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { BasicTooltip } from "@/components/ui/Tooltip";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { useParseInputVariables } from "@/queries";
import { cn } from "@/lib/utils";
import { PromptBlueprint } from "@/types";
import { PromptVersion } from "@/types/apiGetters";
import { getTemplateFormat } from "@/utils/utils";

const VISIBLE_VARIABLE_COUNT = 3;

interface PromptTemplateInputVariablesRowProps {
  promptVersion?: PromptVersion;
  fallbackInputVariables: string[];
  className?: string;
}

const dedupeVariables = (variables: string[]) =>
  Array.from(new Set(variables.map(variable => variable.trim()).filter(Boolean)));

const formatVariableForDisplay = (variable: string, templateFormat: "f-string" | "jinja2") =>
  templateFormat === "jinja2" ? `{{${variable}}}` : `{${variable}}`;

const InputVariableChip = ({
  variable,
  templateFormat,
  className,
}: {
  variable: string;
  templateFormat: "f-string" | "jinja2";
  className?: string;
}) => {
  const displayVariable = formatVariableForDisplay(variable, templateFormat);
  const textRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = textRef.current;
    if (!element) {
      return;
    }

    const checkTruncation = () => {
      if (!element) {
        return;
      }
      setIsTruncated(element.scrollWidth > element.clientWidth + 1);
    };

    checkTruncation();

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [displayVariable]);

  return (
    <span
      className={cn(inputVariableChipClassName, className)}
      data-input-variable="true"
      data-input-variable-key={variable}
    >
      <BasicTooltip content={displayVariable} shouldShow={isTruncated}>
        <span ref={textRef} className="block min-w-0 truncate">
          {displayVariable}
        </span>
      </BasicTooltip>
    </span>
  );
};

const OverflowVariablesPopover = ({
  variables,
  hiddenCount,
  templateFormat,
}: {
  variables: string[];
  hiddenCount: number;
  templateFormat: "f-string" | "jinja2";
}) => {
  if (hiddenCount === 0) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="tinyButton"
          className="h-7 shrink-0 rounded-md border border-blue-100 bg-blue-50 px-2.5 text-xs font-medium text-blue-600 shadow-none hover:border-blue-200 hover:bg-blue-100/70 hover:text-blue-700 focus-visible:ring-blue-200"
        >
          +{hiddenCount}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">
        <div className="mb-2 flex items-center justify-between gap-2 px-1 text-xs font-medium text-gray-500">
          <span className="flex items-center gap-1.5">
            <Variable className="h-3.5 w-3.5" />
            Input Variables
          </span>
          <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-500">
            {variables.length}
          </span>
        </div>
        <div className="flex max-h-56 flex-wrap gap-1.5 overflow-y-auto pr-1 pt-0.5">
          {variables.map(variable => (
            <InputVariableChip
              key={variable}
              variable={variable}
              templateFormat={templateFormat}
              className="w-fit max-w-full"
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const PromptTemplateInputVariablesRow = ({
  promptVersion,
  fallbackInputVariables,
  className,
}: PromptTemplateInputVariablesRowProps) => {
  const auth = useAuth();
  const activeWorkspaceId = useUser()?.activeWorkspaceId;
  const userToken = auth?.userToken ?? "";

  const templateFormat = useMemo(
    () => (promptVersion ? getTemplateFormat(promptVersion.prompt_template) : "f-string"),
    [promptVersion],
  );

  const promptBlueprint = useMemo<PromptBlueprint | undefined>(() => {
    if (!promptVersion) {
      return undefined;
    }

    return {
      prompt_template: promptVersion.prompt_template,
      provider_base_url_name: promptVersion.provider_base_url_name,
      commit_message: promptVersion.commit_message ?? undefined,
      metadata: promptVersion.metadata,
      report_id: promptVersion.report_id ?? undefined,
      provider_id: promptVersion.provider_id ?? undefined,
      input_variable_sets: promptVersion.input_variable_sets,
      input_variable_set_names: promptVersion.input_variable_set_names ?? undefined,
    };
  }, [promptVersion]);

  const detectedInputVariables = useParseInputVariables(userToken, promptBlueprint, {
    enabled: Boolean(userToken && activeWorkspaceId && promptBlueprint),
  });

  const variables = useMemo(() => {
    const detectedVariables = detectedInputVariables.data?.success
      ? detectedInputVariables.data.input_variables
      : [];

    return dedupeVariables(
      detectedVariables.length > 0 ? detectedVariables : fallbackInputVariables,
    );
  }, [detectedInputVariables.data, fallbackInputVariables]);

  const overflowVariables = variables.slice(VISIBLE_VARIABLE_COUNT);
  const isDetecting = detectedInputVariables.isLoading || detectedInputVariables.isFetching;
  const hasDetectionError = Boolean(
    detectedInputVariables.error || detectedInputVariables.data?.success === false,
  );

  if (!promptVersion) {
    return null;
  }

  let variablesContent: ReactNode;

  if (isDetecting && variables.length === 0) {
    variablesContent = (
      <div className="flex min-w-0 items-center gap-1.5">
        <Skeleton className="h-6 w-16 rounded-md" />
        <Skeleton className="h-6 w-20 rounded-md" />
        <Skeleton className="hidden h-6 w-14 rounded-md sm:block" />
      </div>
    );
  } else if (variables.length > 0) {
    const visibleVariables = variables.slice(0, VISIBLE_VARIABLE_COUNT);

    variablesContent = (
      <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
        <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
          {visibleVariables.map(variable => (
            <InputVariableChip key={variable} variable={variable} templateFormat={templateFormat} />
          ))}
        </div>
        <OverflowVariablesPopover
          variables={variables}
          hiddenCount={overflowVariables.length}
          templateFormat={templateFormat}
        />
      </div>
    );
  } else {
    const emptyStateText = hasDetectionError
      ? "Couldn’t detect variables"
      : "No variables detected";
    variablesContent = <span className="truncate text-[11px] text-gray-400">{emptyStateText}</span>;
  }

  return (
    <div
      className={cn(
        "flex min-w-0 max-w-full items-center justify-start gap-2 overflow-hidden rounded-md border border-gray-100 bg-white px-2.5 py-1.5",
        className,
      )}
    >
      <div className="flex shrink-0 items-center gap-1.5 text-sm font-medium text-gray-500">
        <Variable className="h-4 w-4 text-gray-400" />
        <span>Input Variables</span>
      </div>
      <div className="flex min-w-0 flex-1 items-center justify-start gap-1.5 overflow-hidden">
        {variablesContent}
      </div>
    </div>
  );
};

export default PromptTemplateInputVariablesRow;
